/**
 * VM Control Actions
 */

import { api, ApiClientError } from '../api/client.js';
import { needsPassphrase } from './passphrase.js';
import type { VmCommandTask, VmTask } from '../api/types.js';
import { apiCall } from '../api/helpers.js';
import { getVmById } from '../globals.js';
import { getCurrentUser } from '../app/init.js';
import { showPortForwards } from './portfwd.js';
import {
  declareLoading,
  declareLoaded,
  notificationDialog,
  confirmDialog,
  errorDialog,
} from '../ui/dialogs.js';
import { escapeHtml } from '../utils.js';
import { rdpConnectMethod } from './rdp-connect.js';

interface PassphraseResult {
  passphrase: string;  // Empty string means "use stored"
  store: boolean;
}

/**
 * Prompt the user for a LUKS disk encryption passphrase.
 * @param title Dialog title
 * @param alreadyStored Whether a passphrase is already stored in the keystore
 * @returns Passphrase result, or null if the user cancelled
 */
function promptPassphrase(title: string, alreadyStored: boolean): Promise<PassphraseResult | null> {
  return new Promise((resolve) => {
    const storeChecked = alreadyStored ? 'checked' : '';
    const content = `
      <div class="vm-form">
        <label>Passphrase</label>
        <div class="form-value">
          <input type="password" id="luks-passphrase" autocomplete="current-password"
                 style="width: 100%">
        </div>
        <div style="grid-column: 1 / -1; margin-top: 4px;">
          <label style="font-weight: normal; cursor: pointer;">
            <input type="checkbox" id="luks-store" ${storeChecked}>
            Store passphrase for auto-start
          </label>
        </div>
        <p style="font-size: 0.9em; color: var(--color-text-muted, #666); grid-column: 1 / -1;">
          This VM has encrypted disks. Enter the LUKS passphrase to unlock them.
          ${alreadyStored ? 'A passphrase is already stored — click "Use Stored" to use it.' : ''}
        </p>
      </div>
    `;

    const buttons: Record<string, (this: HTMLElement) => void> = {};

    if (alreadyStored) {
      buttons['Use Stored'] = function () {
        $(this).dialog('close');
        resolve({ passphrase: '', store: true });
      };
    }

    buttons['Unlock'] = function () {
      const pass = ($('#luks-passphrase').val() as string) || '';
      if (!pass) {
        alert('Please enter a passphrase');
        return;
      }
      const store = ($('#luks-store').is(':checked'));
      $(this).dialog('close');
      resolve({ passphrase: pass, store });
    };

    buttons['Cancel'] = function () {
      $(this).dialog('close');
      resolve(null);
    };

    $('<div></div>')
      .appendTo('body')
      .html(content)
      .dialog({
        modal: true,
        title,
        width: 400,
        buttons,
        close: function () {
          $(this).remove();
        },
        open: function () {
          setTimeout(() => $('#luks-passphrase').trigger('focus'), 100);
        },
      });
  });
}

/**
 * Build the start body from a passphrase result, or undefined if no passphrase needed.
 */
function buildStartBody(result: PassphraseResult | null | undefined): { passphrase?: string; store_passphrase?: boolean } | undefined {
  if (!result) return undefined;
  const body: { passphrase?: string; store_passphrase?: boolean } = {};
  if (result.passphrase) body.passphrase = result.passphrase;
  if (result.store) body.store_passphrase = true;
  return Object.keys(body).length > 0 ? body : undefined;
}

/**
 * Wait for a VM command's task to finish (#186 §5). The row itself follows
 * the VM over the status socket; this only decides which dialog to show.
 * Resolves with the terminal task, the last one seen if it is still running
 * at the deadline, or null if the daemon no longer knows it (it restarted).
 */
async function awaitTask(resp: VmCommandTask | null): Promise<VmTask | null> {
  if (!resp || !resp.task_id) return null;
  const deadline = Date.now() + ((resp.timeout_sec ?? 90) + 15) * 1000;
  let last: VmTask | null = null;
  for (;;) {
    try {
      last = await api.getTask(resp.task_id);
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'TASK_UNKNOWN') return null;
      throw error;
    }
    if (last.state !== 'running' || Date.now() >= deadline) return last;
    await new Promise(r => setTimeout(r, 1000));
  }
}

/** Show a failed task's reason; returns true if the task succeeded. */
function reportTask(task: VmTask | null, title: string): boolean {
  if (!task) {
    notificationDialog('vmsmand restarted during the operation; check the VM\'s state in the list.');
    return false;
  }
  if (task.state === 'succeeded') return true;
  if (task.state === 'timed_out' && (task.action === 'stop' || task.action === 'restart')) {
    notificationDialog(
      'The VM did not shut down: the guest ignored the request (it may be booting or installing updates; check using "Console"). ' +
      'The stop stays pending and completes if the guest shuts down. You can stop again, use "Keep running", or "Force Off" as a last resort.'
    );
    return false;
  }
  if (task.state === 'running') {
    notificationDialog(`The ${task.action} is still in progress (${task.phase ?? 'working'}).`);
    return false;
  }
  errorDialog(`\u26A0\uFE0F ${title}`, task.error ?? `${task.action} failed`);
  return false;
}

/** Ask for the passphrase and start again, reporting the outcome under `title`. */
async function startWithPassphrase(id: string, title: string): Promise<void> {
  const vm = getVmById(id);
  const result = await promptPassphrase('Unlock Encrypted Disks', !!vm?.passphrase_stored);
  if (result === null) return;
  declareLoading();
  const resp = await apiCall(api.startVm(id, buildStartBody(result)), title);
  if (resp) reportTask(await awaitTask(resp), title);
  declareLoaded();
}

/**
 * Start a VM.
 * Attempts start without a passphrase first, and asks for one when the
 * task reports that the disks need it.
 */
export async function vmStart(id: string): Promise<void> {
  declareLoading();
  const resp = await apiCall(api.startVm(id), 'VM Start Failed');
  const task = resp ? await awaitTask(resp) : null;
  declareLoaded();
  if (!resp) return;
  if (needsPassphrase(task)) return startWithPassphrase(id, 'VM Start Failed');
  reportTask(task, 'VM Start Failed');
}

/**
 * Stop a VM gracefully
 */
export async function vmStop(id: string): Promise<void> {
  confirmDialog(
    'Are you sure you want to stop this VM? Data could be lost if people are on it. The guest gets up to 30 seconds to shut down cleanly.',
    async () => {
      const resp = await apiCall(api.stopVm(id), 'VM Stop Failed');
      if (!resp) return;
      // The row shows "Stopping..." from now on; report the outcome.
      if (reportTask(await awaitTask(resp), 'VM Stop Failed')) {
        notificationDialog('The VM successfully shut down cleanly');
      }
    }
  );
}

/**
 * Restart a VM (graceful stop then start, one task on the server)
 */
export async function vmRestart(id: string): Promise<void> {
  confirmDialog(
    'Are you sure you want to restart this VM? Data could be lost if people are on it. The guest gets up to 30 seconds to shut down cleanly.',
    async () => {
      const resp = await apiCall(api.restartVm(id), 'VM Restart Failed');
      if (!resp) return;
      const task = await awaitTask(resp);
      // Encrypted disks without a stored passphrase: the restart stops the VM
      // and then can't start it; ask and start it here.
      if (needsPassphrase(task)) return startWithPassphrase(id, 'VM Restart Failed');
      reportTask(task, 'VM Restart Failed');
    }
  );
}

/**
 * Force restart a VM (kill + start)
 */
export async function vmForceRestart(id: string): Promise<void> {
  confirmDialog(
    'Are you sure you want to force restart? This immediately kills the VM (like pulling the power cord) and restarts it. Do this only if the VM is stuck.',
    async () => {
      declareLoading();
      const killed = await apiCall(api.killVm(id), 'Force Restart Failed');
      if (!killed || killed.state !== 'succeeded') { declareLoaded(); return; }
      const resp = await apiCall(api.startVm(id), 'Force Restart Failed');
      const task = resp ? await awaitTask(resp) : null;
      declareLoaded();
      if (!resp) return;
      if (needsPassphrase(task)) return startWithPassphrase(id, 'Force Restart Failed');
      reportTask(task, 'Force Restart Failed');
    }
  );
}

/**
 * Force power off a VM
 */
export async function vmForcePowerOff(id: string): Promise<void> {
  confirmDialog(
    'Are you sure you want to force power off? Do this only if the VM is stuck',
    async () => {
      declareLoading();
      const resp = await apiCall(api.killVm(id), 'Force Power Off Failed');
      if (resp && resp.state === 'failed')
        errorDialog('\u26A0\uFE0F Force Power Off Failed', resp.error ?? 'kill failed');
      declareLoaded();
    }
  );
}

/**
 * Keep running a VM whose guest ignored a stop (cancel the pending stop)
 */
export async function vmCancelStop(id: string): Promise<void> {
  confirmDialog(
    'Keep this VM running? This cancels the pending stop: if the guest later shuts down by itself it will be treated as a reboot again.',
    async () => {
      declareLoading();
      await apiCall(api.cancelStopVm(id), 'Keep Running Failed');
      declareLoaded();
    }
  );
}

/**
 * Pause a VM
 */
export async function vmPause(id: string): Promise<void> {
  declareLoading();
  const resp = await apiCall(api.pauseVm(id), 'Pause Failed');
  if (resp) reportTask(await awaitTask(resp), 'Pause Failed');
  declareLoaded();
}

/**
 * Resume a paused VM
 */
export async function vmResume(id: string): Promise<void> {
  declareLoading();
  const resp = await apiCall(api.resumeVm(id), 'Resume Failed');
  if (resp?.task_id) reportTask(await awaitTask(resp), 'Resume Failed');
  declareLoaded();
}

/**
 * Delete a VM
 */
export async function vmDelete(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) return;

  if (vm.status === 'running' || vm.status === 'stop_pending') {
    notificationDialog('Cannot delete a running VM. Stop it first.');
    return;
  }

  let msg = `Are you sure you want to delete <strong>${escapeHtml(vm.name)}</strong>?`;

  // Warn if this VM is owned by a remote peer
  if (vm.owner_host) {
    msg += `<p style="color:#b94a48;margin-top:10px;">This VM is owned by peer <strong>${escapeHtml(vm.owner_host)}</strong>. ` +
           `Deleting it locally may cause the peer to re-create it.</p>`;
  }

  // Warn if this VM is delegated to a remote host
  if (vm.host) {
    msg += `<p style="color:#b94a48;margin-top:10px;">This VM is delegated to host <strong>${escapeHtml(vm.host)}</strong>. ` +
           `The remote copy will also be deleted.</p>`;
  }

  // Show preserved disk volumes
  if (vm.disks && vm.disks.length > 0) {
    msg += `<p style="margin-top:10px;">The following disk volumes will <strong>not</strong> be deleted:</p><ul>`;
    for (const disk of vm.disks) {
      msg += `<li><code>${escapeHtml(disk.path)}</code></li>`;
    }
    msg += `</ul>`;
  }

  msg += `<p style="margin-top:10px;">This action removes only the VM configuration.</p>`;

  confirmDialog(msg, async () => {
    declareLoading();
    await apiCall(api.deleteVm(id), 'Delete Failed');
    declareLoaded();
  }, undefined, true);
}

/**
 * Open VNC console for a VM
 */
export async function vmConnectVnc(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  declareLoading();
  const vncInfo = await apiCall(api.getVmVnc(id), 'VNC Connection Failed');
  declareLoaded();
  if (!vncInfo) return;

  // Import and use VNC console module
  // Pass the VM's host for fetching resources from the correct delegated host
  const { openVncConsole } = await import('../vnc/console.js');
  const vmData = vm as unknown as Record<string, unknown>;
  const vmHost = vmData.host as string | undefined;
  openVncConsole(vm.uuid, vm.name, vncInfo, vmHost);
}

/**
 * Open guest VNC console for a VM (connects to VNC server inside the guest)
 */
export async function vmConnectGuestVnc(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  declareLoading();
  const info = await apiCall(api.getVmGuestVnc(id), 'Guest VNC Connection Failed');
  declareLoaded();
  if (!info) return;

  if (!info.available) {
    notificationDialog('Guest VNC not available: guest IP could not be resolved or VNC port (5900) is not reachable');
    return;
  }

  const { openVncConsole } = await import('../vnc/console.js');
  const vmData = vm as unknown as Record<string, unknown>;
  const vmHost = vmData.host as string | undefined;
  openVncConsole(vm.uuid, vm.name, { host: info.guest_ip, port: info.port },
                 vmHost, `/ws/vm/${vm.uuid}/guest-vnc`, true);
}

/**
 * How long Connect waits for its .rdp download to reach the server before
 * offering the "Open connection" button (#263). The download is a ~200-byte
 * response from the host the page came from, so it normally lands well
 * inside this.
 */
const RDP_DOWNLOAD_WAIT_MS = 3000;
const RDP_DOWNLOAD_POLL_MS = 100;

/** A fresh nonce for one download: 16 random bytes, base64url. */
function newRdpDownloadNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Start the .rdp download for a VM by clicking a download link. The request
 * authenticates with a short-lived cookie scoped to the download path, since
 * a navigation carries no Authorization header.
 */
function startRdpDownload(id: string, vmName: string, nonce?: string): void {
  const token = api.getToken();
  if (token) {
    document.cookie = `vmsman_download_token=${encodeURIComponent(token)}; path=/api/vm/${id}/rdp/download; max-age=30; SameSite=Strict`;
  }
  const a = document.createElement('a');
  a.href = api.getVmRdpDownloadUrl(id, nonce);
  a.download = `${vmName}.rdp`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Wait for the cookie the download response sets for this nonce. Its
 * absence means the browser never sent the request: Chrome holds back a
 * repeated download it counts as automatic, and one started after an await
 * is outside the click's user gesture.
 */
async function rdpDownloadReachedServer(nonce: string): Promise<boolean> {
  const name = `vmsman_rdp_fetched_${nonce}`;
  const deadline = Date.now() + RDP_DOWNLOAD_WAIT_MS;
  for (;;) {
    if (document.cookie.split('; ').includes(`${name}=1`)) {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Strict`;
      return true;
    }
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, RDP_DOWNLOAD_POLL_MS));
  }
}

/**
 * The download didn't start: offer a button whose click (a user gesture)
 * the browser lets download.
 */
function showRdpDownloadFallback(id: string, vmName: string): void {
  const content = `
    <div>
      <p>Your browser didn't start the download for <strong>${escapeHtml(vmName)}</strong>.
        Click <strong>Open connection</strong> to download it.</p>
      <p style="font-size: 0.85em; color: var(--color-text-muted);">
        To skip this step, allow automatic downloads for this site in your
        browser (the icon at the right of the address bar).
      </p>
    </div>
  `;
  $('<div></div>')
    .appendTo('body')
    .html(content)
    .dialog({
      modal: true,
      title: 'RDP Connection',
      zIndex: 10000,
      autoOpen: true,
      width: 400,
      resizable: false,
      buttons: {
        'Open connection': function () {
          startRdpDownload(id, vmName);
          $(this).dialog('close');
        },
        Close: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Connect to a VM via RDP (download .rdp file or show connection info)
 */
export async function vmConnectRdp(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  declareLoading();
  const rdpInfo = await apiCall(api.connectVmRdp(id), 'RDP Connection Failed');
  declareLoaded();
  if (!rdpInfo) return;

  const user = getCurrentUser();
  const downloadPref = user?.settings?.download_rdp !== false;

  if (rdpConnectMethod(rdpInfo, downloadPref) === 'download') {
    // This runs after an await, so the browser may hold the download back;
    // if it never reaches the server, offer the button instead
    const nonce = newRdpDownloadNonce();
    startRdpDownload(id, vm.name, nonce);
    if (!(await rdpDownloadReachedServer(nonce))) {
      console.warn(`RDP download for ${vm.name} did not start within ${RDP_DOWNLOAD_WAIT_MS} ms; offering the button`);
      showRdpDownloadFallback(id, vm.name);
    }
  } else {
    const rdpUri = `rdp://full%20address=s:${encodeURIComponent(rdpInfo.host)}:${rdpInfo.port}`;
    const content = `
      <div>
        <p>RDP connection ready for <strong>${escapeHtml(vm.name)}</strong>.</p>
        <table class="connection-info" style="margin: 10px 0;">
          <tr><td><strong>Host:</strong></td><td>${escapeHtml(rdpInfo.host)}</td></tr>
          <tr><td><strong>Port:</strong></td><td>${rdpInfo.port}</td></tr>
        </table>
        <p><a href="${escapeHtml(rdpUri)}">Open in RDP Client</a></p>
        <p style="font-size: 0.85em; color: var(--color-text-muted);">
          Or connect manually using your RDP client with the address above.
        </p>
      </div>
    `;

    $('<div></div>')
      .appendTo('body')
      .html(content)
      .dialog({
        modal: true,
        title: 'RDP Connection',
        zIndex: 10000,
        autoOpen: true,
        width: 400,
        resizable: false,
        buttons: {
          'Download .rdp': function () {
            startRdpDownload(id, vm.name);
          },
          Close: function () {
            $(this).dialog('close');
          },
        },
        close: function () {
          $(this).remove();
        },
      });
  }
}

/**
 * Open serial console for a VM
 */
export async function vmOpenSerial(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  const { openSerialConsole } = await import('../serial/console.js');
  openSerialConsole(vm.uuid, vm.name);
}

/**
 * Open QEMU monitor for a VM
 */
export async function vmOpenMonitor(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }

  // Import and use monitor module
  const { openMonitorConsole } = await import('../monitor/terminal.js');
  openMonitorConsole(vm.uuid, vm.name);
}

/**
 * Show port forwards management panel for a VM
 */
export async function vmPortForwards(id: string): Promise<void> {
  const vm = getVmById(id);
  if (!vm) {
    notificationDialog('VM not found');
    return;
  }
  showPortForwards(vm.uuid, vm.name);
}

// Export functions to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vmStart = vmStart;
  (window as unknown as Record<string, unknown>).vmStop = vmStop;
  (window as unknown as Record<string, unknown>).vmRestart = vmRestart;
  (window as unknown as Record<string, unknown>).vmForceRestart = vmForceRestart;
  (window as unknown as Record<string, unknown>).vmForcePowerOff = vmForcePowerOff;
  (window as unknown as Record<string, unknown>).vmCancelStop = vmCancelStop;
  (window as unknown as Record<string, unknown>).vmPause = vmPause;
  (window as unknown as Record<string, unknown>).vmResume = vmResume;
  (window as unknown as Record<string, unknown>).vmDelete = vmDelete;
  (window as unknown as Record<string, unknown>).vmConnectVnc = vmConnectVnc;
  (window as unknown as Record<string, unknown>).vmConnectGuestVnc = vmConnectGuestVnc;
  (window as unknown as Record<string, unknown>).vmConnectRdp = vmConnectRdp;
  (window as unknown as Record<string, unknown>).vmOpenMonitor = vmOpenMonitor;
  (window as unknown as Record<string, unknown>).vmOpenSerial = vmOpenSerial;
  (window as unknown as Record<string, unknown>).vmPortForwards = vmPortForwards;
}
